import { Box, Button, Typography, Card, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { toast } from "react-toastify";
import { GeoFileType, KeyedShapefile, RogoFeatures } from "../../../../utilities/types";
import { ListOfFiles } from "./ListOfFiles";
import { KeyGenerationSelector } from "./KeyGenerationSelector";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FeatureCollection } from "geojson";
import { Oops } from "../../../../utilities/oops";
import { Add, NotInterested } from "@mui/icons-material";
import { convertFilesIntoBuffer, GeoData } from "@rogoag/shared-ts";

interface FileTaggingAndSplittingProps {
	keyedShapefileMap: Map<string, KeyedShapefile[]>;
	setKeyedShapefileMap: React.Dispatch<React.SetStateAction<Map<string, KeyedShapefile[]>>>;
	selectableFiles: Record<string, Record<GeoFileType, File[]>>;
	setSelectableFiles: React.Dispatch<React.SetStateAction<Record<string, Record<GeoFileType, File[]>>>>;
	originalShapefiles: File[];
	setOriginalShapefiles: React.Dispatch<React.SetStateAction<File[]>>;
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export function FileTaggingAndSplitting(props: FileTaggingAndSplittingProps) {
	return (
		<>
			<Grid size={12}>
				<Box display={"flex"}>
					<Button
						style={{ width: "80%", marginRight: 5 }}
						variant="contained"
						onClick={async () => {
							const loadingFiles = toast.loading("Loading files...", { closeButton: true });
							try {
								// show file dialog
								const fileInput = document.createElement("input");
								fileInput.type = "file";
								fileInput.accept = ".zip";
								fileInput.multiple = true;
								fileInput.onchange = (event) => {
									event.preventDefault();
									props.setLoading(true);
									// @ts-ignore
									const newFiles: File[] = Array.from(event.target.files);
									const oldFiles = [...props.originalShapefiles];
									for (const newFile of newFiles) {
										if (oldFiles.find((oldFile) => oldFile.name === newFile.name)) {
											// remove from oldFiles
											const index = oldFiles.findIndex((oldFile) => oldFile.name === newFile.name);
											oldFiles.splice(index, 1);
										}
									}

									const allFiles = [...oldFiles, ...newFiles];

									const asyncProcessing = async () => {
										const fileMap = await GeoData.extractGroupedFiles(allFiles);

										props.setOriginalShapefiles(allFiles);

										const features: RogoFeatures[] = [];
										const featureCollections: FeatureCollection[] = [];
										const keyedShapefiles = await Promise.all(
											Object.keys(fileMap).map(async (fullPath) => {
												const shapefiles = fileMap[fullPath];
												if (!fullPath.endsWith(".zip")) {
													fullPath = fullPath + ".zip";
												}
												// zip shapefiles into single file
												const shpBuffer = await convertFilesIntoBuffer(shapefiles);
												const geoJSON = await GeoData.toGeoJSON(shpBuffer, "SHP");
												featureCollections.push(geoJSON);
												// @ts-ignore
												const shapeCounts: Record<string, number> = {};

												if (!geoJSON.features.length) {
													return [fullPath, null] as const;
												}

												geoJSON.features.forEach((feature) => {
													// @ts-ignore
													features.push(feature);
													const type = feature.geometry.type;
													if (!shapeCounts[type]) {
														shapeCounts[type] = 0;
													}
													shapeCounts[type]++;
												});
												// console.log(shapeCounts);
												const shpProperties: Record<string, string> = { ...geoJSON.features[0].properties };
												// @ts-ignore
												const dbfKeys = Object.keys(shpProperties).sort();
												const folderLevels = fullPath.split("/");
												const types = Object.keys(shapeCounts).sort();
												const groupKey = `${folderLevels.length},${dbfKeys.join(",")},${types.join(",")}`;
												const file = new File([shpBuffer as BlobPart], fullPath);
												const keyedFile: KeyedShapefile = file as KeyedShapefile;
												keyedFile.geoJSON = geoJSON;
												keyedFile.dbfKeys = dbfKeys;
												keyedFile.dbfValues = Object.values(shpProperties);
												keyedFile.geoType = "Unknown";
												keyedFile.path = fullPath;
												keyedFile.shapeCounts = shapeCounts;

												return [groupKey, keyedFile] as const;
											}),
										);

										const newKeyedShapefiles: Map<string, KeyedShapefile[]> = new Map();

										for (const [groupKey, keyedShapefile] of keyedShapefiles) {
											if (!keyedShapefile) continue;

											if (!newKeyedShapefiles.has(groupKey)) {
												newKeyedShapefiles.set(groupKey, []);
											}
											newKeyedShapefiles.get(groupKey)?.push(keyedShapefile);
										}

										props.setKeyedShapefileMap(newKeyedShapefiles);

										props.setLoading(false);
										toast.dismiss(loadingFiles);
									};

									asyncProcessing();
								};
								fileInput.onclose = () => {
									// console.log(fileInput.files);
								};
								fileInput.click();
							} catch (error) {
								Oops.handleError(error, "SubmitJobs.CSVImportForm.uploadFilesDialog");
								toast.dismiss(loadingFiles);
							}
						}}
					>
						<Add className="icon-in-button" />
						Add Shapefiles
					</Button>
					<Button
						style={{ width: "20%" }}
						variant="contained"
						onClick={async () => {
							props.setKeyedShapefileMap(new Map());
							props.setOriginalShapefiles([]);
						}}
					>
						<NotInterested className="icon-in-button" />
						Clear
					</Button>
					{/* UploadTypes */}
				</Box>
			</Grid>
			{!!props.originalShapefiles.length && (
				<>
					<Grid size={12}>
						<Accordion defaultExpanded={true}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="h5">{`Original Files (${props.originalShapefiles.length} uploaded)`}</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<ListOfFiles
									files={props.originalShapefiles}
									truncate={3}
									onClear={(file: File) => {
										const newFiles = [...props.originalShapefiles];
										const index = newFiles.findIndex((f) => f.name === file.name);
										newFiles.splice(index, 1);
										props.setOriginalShapefiles(newFiles);
									}}
								/>
							</AccordionDetails>
						</Accordion>
					</Grid>
					<Grid size={12}>
						<Accordion defaultExpanded={true}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="h5">File Tagging and Splitting</Typography>
							</AccordionSummary>
							<AccordionDetails>
								{[...props.keyedShapefileMap.keys()].map((groupKey, index) => {
									const shapefiles = props.keyedShapefileMap.get(groupKey);
									if (!shapefiles) return null;

									const dbfKeys = shapefiles[0].dbfKeys;
									const folderTokens = shapefiles[0].path.split("/");
									const folderOptions: string[] = [];
									const optionTooltips: string[][] = [];

									for (let folderLevel = 1; folderLevel <= folderTokens.length; folderLevel++) {
										folderOptions.push("Folder Level " + folderLevel);
										const tooltip = shapefiles.map((shapefile) => {
											const tokens = shapefile.path.split("/");
											return tokens[folderLevel - 1];
										});
										optionTooltips.push(tooltip);
									}

									dbfKeys.forEach((key) => {
										optionTooltips.push(
											[
												...new Set(
													shapefiles
														.map((shapefile) => {
															return shapefile.geoJSON.features
																.map((feature) => {
																	if (!feature.properties) return "";
																	return feature.properties[key] as string;
																})
																.flat();
														})
														.flat(),
												),
											].filter((value) => value !== "" && value !== undefined),
										);
									});

									return (
										<Card elevation={4} style={{ marginBottom: 10, padding: 15 }} key={groupKey}>
											<KeyGenerationSelector
												index={index + 1}
												options={[...folderOptions, ...dbfKeys]}
												inputFiles={shapefiles}
												setOutputFiles={(files, type) => {
													props.setSelectableFiles((prevFiles) => {
														const newFiles = { ...prevFiles };
														if (!newFiles[groupKey]) {
															newFiles[groupKey] = {
																Centroids: [],
																Cores: [],
																Boundary: [],
																Zones: [],
																Unknown: [],
															};
														}
														newFiles[groupKey][type] = files;
														return newFiles;
													});
												}}
												optionTooltips={optionTooltips}
											/>
										</Card>
									);
								})}
							</AccordionDetails>
						</Accordion>
					</Grid>
				</>
			)}
		</>
	);
}
